import type { SearchStore, StoreSearchParams, StoreV2 } from '@libs/common-types';
import { searchStores } from '@libs/api-proxy-client/v2';
import { logger, stringifyStoreId } from '@libs/core';
import { localCache } from './local-cache';
import { getNextApiRoute } from './api';

export const CT_STORES_BASE_URL = getNextApiRoute('stores');

/**
 * We use a default TTL (Time To Live) of 1 hour for caching to ensure data remains relatively
 * fresh and to avoid unnecessary requests during this period.
 */
const DEFAULT_TTL = 60 * 60 * 1000; // TTL = 1hr

/**
 * Create URL friendly slug from city string
 * `City Name //=> city-name`
 */
const getCitySlug = (city = ''): string => city.normalize().split(' ').join('-').toLowerCase();

export const getStoreDetailsPathSegment = ({ City, StoreNumber }: Pick<StoreV2, 'City' | 'StoreNumber'>): string => {
    const citySlug = getCitySlug(City);
    return `${citySlug}-store${stringifyStoreId(StoreNumber)}.html`;
};

/**
 * ## Constructs a URL pathname for store details using the provided store properties.
 *
 * This function takes a subset of the store properties: `CountryAbbreviation`, `StateProvinceAbbreviation`, `City`, and `StoreNumber`.
 * It performs the following steps:
 *
 * 1. Converts the `City` value into a URL-friendly slug using the `getCitySlug` function.
 * 2. Converts the `CountryAbbreviation` and `StateProvinceAbbreviation` to lowercase.
 * 3. Constructs and returns the pathname in the following format:
 *    `/stores/{country}/{state}/{citySlug}-store{StoreNumber}.html`
 *
 * @deprecated Use getStoreDetailsPathname from @libs/core instead
 */
export const getStoreDetailsPathname = (
    store: Pick<StoreV2, 'CountryAbbreviation' | 'StateProvinceAbbreviation' | 'City' | 'StoreNumber'>
): string => {
    const { CountryAbbreviation, StateProvinceAbbreviation } = store;
    return `/stores/${CountryAbbreviation?.toLocaleLowerCase()}/${StateProvinceAbbreviation?.toLowerCase()}/${getStoreDetailsPathSegment(
        store
    )}`;
};

/**
 * Extracts city slug and storeId from path segment
 */
export const parseStoreDetailsPathSegment = (pathSegment: string): { storeId: string; citySlug: string } | null => {
    const pattern = /^([a-z]+(-[a-z]+)*)-store(\d+)\.html$/;
    const match = RegExp(pattern).exec(pathSegment);

    if (!match) {
        return null;
    }

    return {
        citySlug: match[1],
        storeId: match[3],
    };
};

/**
 * Validates that path segment matches store data
 */
export const validateStoreDetailsPathSegment = (pathSegment: string, store: Pick<StoreV2, 'City' | 'StoreNumber'>) =>
    pathSegment === getStoreDetailsPathSegment(store);

/**
 * ## Retrieves a list of stores based on a search params.
 *
 * This asynchronous function performs the following steps:
 *
 * 1. Attempts to retrieve cached data from localStorage using a caching mechanism with a specified time-to-live (TTL).
 *    - If valid cached data is found, it is returned immediately.
 *
 * 2. If no valid cached data is available, the function makes an API request using `searchStores` function from api proxy.
 *    - If the response is successful, the result is cached using `setLocalCache` for future requests.
 *
 * 3. In case of an error during the API call, the error is logged and an empty array is returned.
 */
export const getSearchStores = async ({
    lat,
    lng,
    query,
    radius = 30,
    sku,
    page = 1,
    includeAttributes = false,
}: StoreSearchParams): Promise<SearchStore[]> => {
    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cacheKey = `lat=${lat},lng=${lng},query=${query},radius=${radius},sku=${sku},page=${page},includeAttributes=${includeAttributes}`;
    const cachedResult = await getLocalCache<SearchStore[]>(cacheKey);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const response = await searchStores({
            lat,
            lng,
            query,
            radius,
            sku,
            page,
            includeAttributes,
        });

        if (!response) {
            return [];
        }

        // Set localstorage response data
        await setLocalCache({ key: cacheKey, value: response });

        return response;
    } catch (error) {
        logger.error('Error getSearchStores. %o', error);
        return [];
    }
};
